// frontend/src/components/placements/PlacementRetentionPage.jsx
// This page provides access to post-placement retention tracking (CRP-32)
// using the existing placement_followups system

import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const PlacementRetentionPage = () => {
  const { user } = useContext(AuthContext);
  const apiBase = getApiBaseURL();

  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const loadFollowups = async () => {
    if (!user?.tenant_id) return;

    setLoading(true);
    try {
      const res = await secureAxios.get(`${apiBase}/api/placement-followups`, {
        params: { tenant_id: user.tenant_id },
      });
      setFollowups(res.data);
    } catch (err) {
      console.error("Error loading retention follow-ups:", err);
      toast.error("Failed to load retention follow-ups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFollowups();
  }, [user?.tenant_id]);

  const filteredFollowups = followups.filter((f) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      f.consumer_name?.toLowerCase().includes(search) ||
      f.employer_name?.toLowerCase().includes(search) ||
      f.job_title?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  // Check if a milestone is complete (requires both consumer AND specialist signatures)
  const isMilestoneComplete = (followup, milestone) => {
    switch (milestone) {
      case "week1":
        return !!(
          followup.review_week1_consumer_signature &&
          followup.review_week1_specialist_signature
        );
      case "days30":
        return !!(
          followup.review_30days_consumer_signature &&
          followup.review_30days_specialist_signature
        );
      case "days60":
        return !!(
          followup.review_60days_consumer_signature &&
          followup.review_60days_specialist_signature
        );
      case "days90":
        return !!(
          followup.review_closure_consumer_signature &&
          followup.review_closure_specialist_signature
        );
      default:
        return false;
    }
  };

  const getRetentionStatus = (followup) => {
    // Calculate progress based on signed milestones (each milestone = 25%)
    let completedCount = 0;
    if (isMilestoneComplete(followup, "week1")) completedCount++;
    if (isMilestoneComplete(followup, "days30")) completedCount++;
    if (isMilestoneComplete(followup, "days60")) completedCount++;
    if (isMilestoneComplete(followup, "days90")) completedCount++;

    const progress = (completedCount / 4) * 100;

    // Determine the label based on highest completed milestone
    if (completedCount === 4) {
      return { badge: "bg-success", label: "90 Days", progress: 100, checkmark: true };
    }
    if (isMilestoneComplete(followup, "days60")) {
      return { badge: "bg-info", label: "60 Days", progress, checkmark: false };
    }
    if (isMilestoneComplete(followup, "days30")) {
      return { badge: "bg-warning text-dark", label: "30 Days", progress, checkmark: false };
    }
    if (isMilestoneComplete(followup, "week1")) {
      return { badge: "bg-secondary", label: "Week 1", progress, checkmark: false };
    }
    return { badge: "bg-light text-dark", label: "Not Started", progress: 0, checkmark: false };
  };

  // Get the next milestone that needs to be completed
  const getNextMilestone = (followup) => {
    if (!isMilestoneComplete(followup, "week1")) {
      return { label: "Week 1 Check-in", className: "text-muted" };
    }
    if (!isMilestoneComplete(followup, "days30")) {
      return { label: "30-Day Review", className: "text-primary" };
    }
    if (!isMilestoneComplete(followup, "days60")) {
      return { label: "60-Day Review", className: "text-info" };
    }
    if (!isMilestoneComplete(followup, "days90")) {
      return { label: "90-Day Review", className: "text-warning" };
    }
    return { label: "Complete", className: "text-success" };
  };

  // Calculate stats based on signatures
  const getStats = () => {
    const total = followups.length;
    const complete = followups.filter((f) => isMilestoneComplete(f, "days90")).length;
    const at30Day = followups.filter(
      (f) => isMilestoneComplete(f, "days30") && !isMilestoneComplete(f, "days60")
    ).length;
    const notStarted = followups.filter((f) => !isMilestoneComplete(f, "week1")).length;

    return { total, complete, at30Day, notStarted };
  };

  const handleGeneratePDF = async (followupId, consumerName) => {
    try {
      toast.info("Generating PDF...");
      const res = await secureAxios.get(
        `${apiBase}/api/placement-followups/pdf/${followupId}`,
        {
          params: { tenant_id: user.tenant_id },
        }
      );

      if (res.data.pdf_url) {
        window.open(res.data.pdf_url, "_blank");
        toast.success("PDF generated successfully");
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      toast.error("Failed to generate PDF");
    }
  };

  const stats = getStats();

  return (
    <div className="container my-4">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 mb-md-3 gap-2">
        <h4 className="mb-0 h5">
          <i className="bi bi-calendar-check me-2"></i>
          Retention
        </h4>
      </div>

      {/* Info Card */}
      <div className="alert alert-info mb-2 mb-md-3 py-2 small">
        <i className="bi bi-info-circle me-2"></i>
        <strong>Post-Placement Retention:</strong> Track consumer retention with
        Week 1, 30-day, 60-day, and 90-day follow-up milestones. Complete the
        CRP-32 Job Placement Follow-Up Form for each placement.
      </div>

      {/* Quick Stats */}
      {!loading && followups.length > 0 && (
        <div className="row g-2 mb-2 mb-md-3">
          <div className="col-md-3">
            <div className="card bg-light">
              <div className="card-body text-center py-2">
                <h5 className="mb-0 h6">{stats.total}</h5>
                <small className="text-muted">Total Records</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success text-white">
              <div className="card-body text-center py-2">
                <h5 className="mb-0 h6">{stats.complete}</h5>
                <small>90-Day Completions</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning">
              <div className="card-body text-center py-2">
                <h5 className="mb-0 h6">{stats.at30Day}</h5>
                <small>At 30-Day Mark</small>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-secondary text-white">
              <div className="card-body text-center py-2">
                <h5 className="mb-0 h6">{stats.notStarted}</h5>
                <small>Not Started</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="row g-2 mb-2 mb-md-3">
        <div className="col-md-6">
          <div className="input-group input-group-sm">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search by consumer, employer, or job title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                onClick={() => setSearchTerm("")}
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
        </div>
        <div className="col-12 col-md-6 text-end mt-2 mt-md-0">
          <Link to="/crm/job-placement" className="btn btn-primary">
            <i className="bi bi-plus-circle me-2"></i>
            Manage Follow-ups
          </Link>
        </div>
      </div>

      {/* Follow-ups Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : filteredFollowups.length === 0 ? (
        <div className="text-center py-5">
          <i
            className="bi bi-calendar-check text-muted"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3 text-muted">No Retention Records Found</h5>
          <p className="text-muted">
            {searchTerm
              ? "Try adjusting your search criteria"
              : "Create a retention follow-up from the Job Placement section"}
          </p>
          <Link to="/crm/job-placement" className="btn btn-primary">
            <i className="bi bi-plus-circle me-2"></i>
            Go to Job Placement
          </Link>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="table-responsive d-none d-lg-block">
            <table className="table table-hover table-sm">
              <thead className="table-light">
                <tr>
                  <th>Consumer</th>
                  <th>Employer</th>
                  <th>Job Title</th>
                  <th>Date Placed</th>
                  <th>Retention Progress</th>
                  <th>Next Milestone</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFollowups.map((followup) => {
                  const status = getRetentionStatus(followup);
                  const nextMilestone = getNextMilestone(followup);
                  return (
                    <tr key={followup.followup_id}>
                      <td>
                        <strong>{followup.consumer_name}</strong>
                      </td>
                      <td>{followup.employer_name || "-"}</td>
                      <td>{followup.job_title || "-"}</td>
                      <td>{formatDate(followup.date_placed)}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div
                            className="progress flex-grow-1 me-2"
                            style={{ height: "8px" }}
                          >
                            <div
                              className="progress-bar bg-success"
                              role="progressbar"
                              style={{ width: `${status.progress}%` }}
                            ></div>
                          </div>
                          <span className={`badge ${status.badge}`}>
                            {status.label}
                            {status.checkmark && (
                              <i className="bi bi-check ms-1"></i>
                            )}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={nextMilestone.className}>
                          {nextMilestone.label}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="btn-group btn-group-sm">
                          <Link
                            to={`/crm/job-placement?job_placement_id=${followup.job_placement_id}&followup_id=${followup.followup_id}&action=edit-followup`}
                            className="btn btn-outline-primary"
                            title="Edit CRP-32"
                          >
                            <i className="bi bi-pencil"></i>
                          </Link>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() =>
                              handleGeneratePDF(
                                followup.followup_id,
                                followup.consumer_name
                              )
                            }
                            title="Generate PDF"
                          >
                            <i className="bi bi-file-pdf"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="d-block d-lg-none">
            {filteredFollowups.map((followup) => {
              const status = getRetentionStatus(followup);
              const nextMilestone = getNextMilestone(followup);
              return (
                <div key={followup.followup_id} className="card mb-2">
                  <div className="card-body p-2">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="fw-bold text-truncate">
                          {followup.consumer_name}
                        </div>
                        <div className="small text-muted text-truncate">
                          {followup.job_title || "No job title"} @ {followup.employer_name || "Unknown"}
                        </div>
                      </div>
                      <span className={`badge ${status.badge} ms-2`}>
                        {status.label}
                        {status.checkmark && <i className="bi bi-check ms-1"></i>}
                      </span>
                    </div>
                    <div className="mb-2">
                      <div className="progress" style={{ height: "6px" }}>
                        <div
                          className="progress-bar bg-success"
                          style={{ width: `${status.progress}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="small">
                        <span className="text-muted me-2">
                          <i className="bi bi-calendar me-1"></i>
                          {formatDate(followup.date_placed)}
                        </span>
                        <span className={nextMilestone.className}>
                          Next: {nextMilestone.label}
                        </span>
                      </div>
                      <div className="btn-group btn-group-sm">
                        <Link
                          to={`/crm/job-placement?job_placement_id=${followup.job_placement_id}&followup_id=${followup.followup_id}&action=edit-followup`}
                          className="btn btn-outline-primary btn-sm"
                        >
                          <i className="bi bi-pencil"></i>
                        </Link>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() =>
                            handleGeneratePDF(
                              followup.followup_id,
                              followup.consumer_name
                            )
                          }
                        >
                          <i className="bi bi-file-pdf"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Summary */}
      {!loading && filteredFollowups.length > 0 && (
        <div className="text-muted small">
          Showing {filteredFollowups.length} retention record
          {filteredFollowups.length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
};

export default PlacementRetentionPage;